import { SparkleIcon } from "assets/icons/Sparkle";
import { QuestionsPill } from "components/QuestionsPill";
import { UserRole } from "constants/userRole";
import { useChatWidgetPresence } from "context/ChatWidgetPresenceContext";
import useIsMobile from "hooks/useIsMobile";
import { confirmDialog } from "primereact/confirmdialog";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  useEndStaffChatMutation,
  useGetAssignedChatModelQuery,
} from "store/queries/chat";
import { ChatSupportStatus, IChatMessage, ISupportInfo } from "types/Chat";

import {
  browserChatIdStorage,
  buildChatRequestHeaders,
  buildSeenMap,
  chatPreview,
  countUnreadSupportReplies,
  currentPageUrl,
  getMessageKind,
  getSupportBanner,
  hasUnreadSupportReply,
  isStoredChatStale,
  isSupportActive,
  mergeChatMessages,
  newestSupportMessageAt,
  ownStaffChats,
  pickChatToRestore,
  shouldPollSupportChat,
  shouldPollSupportList,
  shouldShowStaffChatWidget,
  supportStatusTag,
  useStoredStaffChatId,
  withoutOptimisticMessage,
  writeSeenAt,
} from "./chatSupport.helpers";
import {
  autoSelectsFirstChat,
  isUnlistedChatId,
  resolveSuggestedQuestions,
  shouldReopenLastChat,
  showsChatList,
} from "./chatWidget.helpers";
import {
  buildStaffGreeting,
  CONTACT_SUPPORT_LABEL,
  END_SUPPORT_CHAT_CONFIRM,
  STAFF_WIDGET_SUBTITLE,
  STAFF_WIDGET_TITLE,
  SUPPORT_CONTACT_FALLBACK,
  SUPPORT_LIST_POLL_INTERVAL_MS,
  SUPPORT_POLL_INTERVAL_MS,
} from "./hospitalStaffBot.constants";
import "./ChatWidget.css";

const API_BASE_URL = (window as any)?.config?.apiUrl || "http://localhost:3000";

interface IChat {
  id: string;
  accountId: string;
  orderId?: string;
  chatModelId: number;
  mostRecentResponseId?: string;
  chatMessages?: IChatMessage[];
  createdAt: string;
  updatedAt: string;
  chatModel?: {
    id: number;
    multipleChatsSupported?: boolean;
    [key: string]: any;
  };
  /** CLE-3043 — staff chats only. */
  supportStatus?: ChatSupportStatus;
  escalatedAt?: string | null;
  supportAgentName?: string | null;
  /** CLE-2851 — who started it; null on a chat from before that was recorded. */
  userId?: string | null;
  /** CLE-3043 — the list's newest support line (GET /chats), for the "New reply" mark. */
  lastSupportMessageAt?: string | null;
}

/** CLE-3043 — a status line the widget shows itself (not stored), in the system-line style. */
const localSystemLine = (content: string): IChatMessage => ({
  role: "assistant",
  author: "system",
  content,
  timestamp: new Date().toISOString(),
});

function SendIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export type ChatBotType = "PatientChatBot" | "ContractManagementBot";

interface IChatWidgetProps {
  accountId: string;
  chatType: ChatBotType;
  shouldAutoOpen?: boolean;
  contextData?: any; // Flexible context data that depends on chat type
  title?: string;
  subtitle?: string;
  welcomeMessage?: string;
  patientFirstName?: string;
  position?: "fixed" | "inline";
}

export function ChatWidget({
  accountId,
  chatType,
  shouldAutoOpen = false,
  contextData,
  title,
  subtitle,
  welcomeMessage,
  patientFirstName,
  position = "fixed",
}: IChatWidgetProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [isOpen, setIsOpen] = useState(false);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const isStaffBot = chatType === "ContractManagementBot";
  // Whether the staff widget's one-off restore (after mounting, or after the account changed) has
  // run (see the restore effect).
  const firstRestoreDoneRef = useRef(false);
  // 2026-10-29 — the staff widget starts from the last chat stored for this account, so a reload or
  // a bookmark gets back to it (it has no chat list to pick it from), and keeps it across reloads:
  // every path that changes it (a chat opened, a new chat created by a message or an escalation, New
  // chat, a refused chat) goes through lastSelectedChatId. If the account changes without a remount,
  // the new account's stored chat is read and the one-off restore runs again for it. The patient
  // widget starts empty and stores nothing.
  const [lastSelectedChatId, setLastSelectedChatId] = useStoredStaffChatId(
    isStaffBot,
    accountId,
    () => {
      firstRestoreDoneRef.current = false;
    }
  );
  const [chats, setChats] = useState<IChat[]>([]);
  // CLE-3043 (Ani, 2026-11-03) — the staff list holds only this user's own chats on this hospital
  // (ownStaffChats); the patient widget lists what it always did.
  const listedChats = useMemo(
    () => (isStaffBot ? ownStaffChats(chats) : chats),
    [isStaffBot, chats]
  );
  const [multipleChatsSupported, setMultipleChatsSupported] = useState<
    boolean | null
  >(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // CLE-3043 — Clear Health support on a staff chat. NONE while the bot answers.
  const [supportStatus, setSupportStatus] = useState<ChatSupportStatus>("NONE");
  const [supportAgentName, setSupportAgentName] = useState<string | null>(null);
  const [escalatedAt, setEscalatedAt] = useState<string | null>(null);
  const [escalationSupport, setEscalationSupport] =
    useState<ISupportInfo | null>(null);
  // Whether the message in flight expects a bot reply (no typing dots while support is on the chat).
  const [pendingBotReply, setPendingBotReply] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  const currentChatIdRef = useRef<string | null>(null);
  currentChatIdRef.current = currentChatId;
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const isPollingRef = useRef(false);
  // CLE-3043 (Ani, 2026-11-03) — bumped whenever a chat's "seen" mark moves, so the history list's
  // "New reply" dots and the pill's badge (read from localStorage) re-render.
  const [seenVersion, setSeenVersion] = useState(0);
  const [endStaffChat] = useEndStaffChatMutation();

  // Get mobile detection hook
  const { isMobile } = useIsMobile();

  // Get auth token and account info from Redux store
  const { publicToken, token } = useSelector((state: any) => state.authSlice);
  const {
    scope,
    email: userEmail,
    name: userName,
  } = useSelector((state: any) => state.userSlice);
  // CLE-3043 — the support poll runs from a setInterval set up when support joined; a closure would keep the
  // token / account of that render, and a rotated Auth0 token would keep polling with the old bearer. The
  // refs always hold this render's values, and chatHeaders() reads them.
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const publicTokenRef = useRef(publicToken);
  publicTokenRef.current = publicToken;
  const accountIdRef = useRef(accountId);
  accountIdRef.current = accountId;
  const warnedPollAuthRef = useRef(false);

  // CLE-2811 — the model assigned to this account, read directly rather than inferred from an
  // existing chat. An account that has never chatted has no chatList[0].chatModel to learn from,
  // so its suggestion chips (and whether a chat list is shown) used to be unknowable. Skipped for
  // the patient widget, which authenticates with a public order token and must not touch this
  // Auth0-guarded endpoint. A failure here is not fatal: everything below falls back.
  const { data: assignedModel } = useGetAssignedChatModelQuery(
    { accountId, chatType },
    { skip: chatType !== "ContractManagementBot" || !accountId }
  );

  // CLE-3043 — the global rollout switch. The patient widget is never gated here.
  const isVisible =
    !isStaffBot ||
    shouldShowStaffChatWidget(scope === UserRole.SUPER_ADMIN, assignedModel);

  // Let the app know a chat widget is on the page (fixed or inline). A staff widget held back by
  // the rollout switch renders nothing, so it does not register. Since CLE-3062 moved the
  // "Powered by" badge into the header nothing reads this; the context is left in place for now.
  const { registerChatWidget } = useChatWidgetPresence();

  useEffect(
    () => (isVisible ? registerChatWidget() : undefined),
    [isVisible, registerChatWidget]
  );

  const support = escalationSupport ?? assignedModel?.support ?? null;

  const resetSupportState = (chat?: IChat) => {
    setSupportStatus(chat?.supportStatus ?? "NONE");
    setSupportAgentName(chat?.supportAgentName ?? null);
    setEscalatedAt(chat?.escalatedAt ?? null);
  };

  /**
   * CLE-3043 — the headers of every chat request (latest token, see tokenRef). The staff widget sends its
   * own `accountId` as the `account` header and never changes the app's current account.
   */
  const chatHeaders = (): Record<string, string> =>
    buildChatRequestHeaders(
      chatType,
      { token: tokenRef.current, publicToken: publicTokenRef.current },
      accountIdRef.current
    );

  // Function to update URL with chatId
  const updateUrlWithChatId = (chatId: string | null) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (chatId) {
      newSearchParams.set("chatId", chatId);
    } else {
      newSearchParams.delete("chatId");
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  // The chat object handleChatSelect last loaded, so the restore effect can tell "already open from
  // this list" from "the list was re-fetched" (shouldReopenLastChat).
  const loadedChatRef = useRef<IChat | null>(null);

  const handleChatSelect = (chat: IChat) => {
    loadedChatRef.current = chat;
    setCurrentChatId(chat.id);
    setSelectedChatId(chat.id);
    setLastSelectedChatId(chat.id);
    setMessages(chat.chatMessages || []);
    if (isStaffBot) resetSupportState(chat);
    updateUrlWithChatId(chat.id);
    // Focus the input when selecting a chat
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // A `?chatId=` in the URL opens that chat — once the user has opened the panel, never by itself on
  // page load (CLE-3043 review: the closed staff widget now reads its list on mount, which must not
  // pop the panel open), and not again while that chat is already the open one (the list poll
  // refreshes `chats` every 15 s and must not reset what is on screen).
  useEffect(() => {
    if (!isOpen) return;
    const chatIdFromUrl = searchParams.get("chatId");
    if (!chatIdFromUrl || chatIdFromUrl === currentChatIdRef.current) return;
    const chat = chats.find((c) => c.id === chatIdFromUrl);
    if (chat) handleChatSelect(chat);
  }, [isOpen, chats, searchParams]);

  // Auto-open chat widget if shouldAutoOpen prop is true
  useEffect(() => {
    if (shouldAutoOpen && !isOpen) {
      setIsOpen(true);
    }
  }, [shouldAutoOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchChats = async () => {
    try {
      const headers = chatHeaders();

      // Use different endpoints based on chat type
      let url;
      if (chatType === "ContractManagementBot") {
        // Use main endpoint for contract management (Auth0 auth)
        const queryParams = `accountId=${accountId}&chatType=${chatType}`;
        url = `${API_BASE_URL}/chats?${queryParams}`;
      } else {
        // Use patient endpoint for patient chat (patient auth)
        let queryParams = `accountId=${accountId}&chatType=${chatType}`;
        if (contextData?.orderExternalId) {
          queryParams += `&orderExternalId=${contextData.orderExternalId}`;
        }
        url = `${API_BASE_URL}/chats/patient?${queryParams}`;
      }

      const response = await fetch(url, {
        credentials: "include",
        headers,
      });
      const result = await response.json();

      // Both /chats and /chats/patient return { data: [...] }
      const chatList = result.data || [];

      setChats(chatList);

      // CLE-2851 — contract chats are per user. A chatId in the URL that is not in this user's list
      // (a colleague's link, or a chat from before chats recorded who started them) can never be
      // opened: drop it, so the widget simply starts a fresh chat. Only once the list really loaded.
      if (
        response.ok &&
        chatType === "ContractManagementBot" &&
        isUnlistedChatId(searchParams.get("chatId"), chatList)
      ) {
        updateUrlWithChatId(null);
      }

      // Set multipleChatsSupported based on the first chat's model configuration
      let chatSupportsMultiple = true;
      if (chatList.length > 0 && chatList[0].chatModel) {
        chatSupportsMultiple =
          chatList[0].chatModel.multipleChatsSupported ?? true;
        setMultipleChatsSupported(chatSupportsMultiple);
      }

      // If multiple chats not supported and we have chats, auto-select the most recent one. Never
      // for the staff bot: its restore effect picks the chat (pickChatToRestore), and the newest
      // chat — possibly an ended one — must not pre-empt the stored last chat.
      if (
        autoSelectsFirstChat(chatType, chatSupportsMultiple) &&
        chatList.length > 0 &&
        !currentChatId
      ) {
        const mostRecentChat = chatList[0]; // Already sorted by createdAt desc
        handleChatSelect(mostRecentChat);
      }
    } catch (error) {
      console.error("Error fetching chats:", error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchChats();
      // Focus the input when the widget opens
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, accountId, chatType]);

  /**
   * CLE-3043 (Ani, 2026-11-03) — re-read the staff chat list and nothing else: no URL changes and no
   * chat opened, so it is safe from a timer (fetchChats would act on the URL as it was when the timer
   * started). While the panel is open the chat on screen keeps its current list object, so the
   * restore effect does not take the fresh copy for a new list and reload that chat over what is
   * shown (the 4 s poll keeps that chat current).
   */
  const refreshStaffChatList = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/chats?accountId=${accountIdRef.current}&chatType=${chatType}`,
        { credentials: "include", headers: chatHeaders() }
      );
      if (!response.ok) return;
      const result = await response.json();
      if (!Array.isArray(result?.data)) return;
      const fresh: IChat[] = result.data;
      setChats((prev) =>
        fresh.map((chat) => {
          const onScreen =
            isOpenRef.current && chat.id === currentChatIdRef.current
              ? prev.find((c) => c.id === chat.id)
              : undefined;
          return onScreen ?? chat;
        })
      );
    } catch (error) {
      console.error("Error refreshing chats:", error);
    }
  };

  // CLE-3043 — the closed staff widget reads its list once, so it knows whether a chat is with
  // support (the poll below) and whether a reply is waiting (the pill's badge). Not when the panel
  // opens on mount (shouldAutoOpen): opening reads the list itself — one read per page view.
  useEffect(() => {
    if (isStaffBot && isVisible && accountId && !isOpen && !shouldAutoOpen) {
      refreshStaffChatList();
    }
  }, [isStaffBot, isVisible, accountId]);

  // CLE-3043 — while one of the user's chats here is with support, re-read the list every 15 s,
  // panel closed or open (the 4 s chat poll still covers the open chat); stops when none is.
  const pollsSupportList =
    isStaffBot && isVisible && shouldPollSupportList({ chats: listedChats });
  useEffect(() => {
    if (!pollsSupportList) return undefined;
    const interval = window.setInterval(
      () => refreshStaffChatList(),
      SUPPORT_LIST_POLL_INTERVAL_MS
    );
    return () => window.clearInterval(interval);
  }, [pollsSupportList]);

  /** CLE-3043 — record that the user has seen the open chat's support lines (only moves forward). */
  const markCurrentChatSeen = (
    chatId: string | null,
    shown: IChatMessage[]
  ) => {
    if (!isStaffBot || !chatId) return;
    if (
      writeSeenAt(browserChatIdStorage(), chatId, newestSupportMessageAt(shown))
    ) {
      setSeenVersion((version) => version + 1);
    }
  };

  // Whenever the open panel shows a chat, what it shows has been seen.
  useEffect(() => {
    if (isOpen) markCurrentChatSeen(currentChatId, messages);
  }, [isStaffBot, isOpen, currentChatId, messages]);

  // CLE-2811 — seed multipleChatsSupported from the assigned model when no chat has answered the
  // question yet. fetchChats above remains the authority whenever the account has chats, so a
  // failed or skipped assigned-model call changes nothing.
  useEffect(() => {
    if (assignedModel && multipleChatsSupported === null) {
      setMultipleChatsSupported(assignedModel.multipleChatsSupported ?? true);
    }
  }, [assignedModel, multipleChatsSupported]);

  /**
   * CLE-3043 — re-read the current chat (GET /chats/:id, which the chat's owner may now call) and
   * merge in what support wrote. Ignored if the user has moved to another chat meanwhile.
   */
  const refreshSupportChat = async (chatId: string) => {
    if (isPollingRef.current) return;
    isPollingRef.current = true;
    try {
      const response = await fetch(`${API_BASE_URL}/chats/${chatId}`, {
        credentials: "include",
        headers: chatHeaders(),
      });
      if (!response.ok) {
        // Once per widget: a refused poll would otherwise be silent, and support's lines would stop arriving.
        if (
          (response.status === 401 || response.status === 403) &&
          !warnedPollAuthRef.current
        ) {
          warnedPollAuthRef.current = true;
          console.warn(
            `Support chat poll refused (${response.status}); support replies will not appear until the page is reloaded.`
          );
        }
        return;
      }
      const result = await response.json();
      const chat = result?.data;
      if (!chat || currentChatIdRef.current !== chatId) return;
      setMessages((prev) => mergeChatMessages(prev, chat.chatMessages));
      setSupportStatus(chat.supportStatus ?? "NONE");
      setSupportAgentName(chat.supportAgentName ?? null);
      setEscalatedAt(chat.escalatedAt ?? null);
    } catch (error) {
      console.error("Error refreshing chat:", error);
    } finally {
      isPollingRef.current = false;
      setNow(Date.now());
    }
  };

  // CLE-3043 — while support is on the chat and the widget is open, poll every 4 s. Stops on CLOSED,
  // when the widget closes, and when the user switches chat (the effect re-runs).
  useEffect(() => {
    if (
      !isStaffBot ||
      !currentChatId ||
      !shouldPollSupportChat({ isOpen, chatId: currentChatId, supportStatus })
    ) {
      return undefined;
    }
    const chatId = currentChatId;
    const interval = window.setInterval(
      () => refreshSupportChat(chatId),
      SUPPORT_POLL_INTERVAL_MS
    );
    return () => window.clearInterval(interval);
  }, [isStaffBot, isOpen, currentChatId, supportStatus]);

  // Restore last selected chat when widget opens and no chatId in URL
  useEffect(() => {
    if (!isOpen || chats.length === 0) return;

    const chatIdFromUrl = searchParams.get("chatId");
    if (chatIdFromUrl) {
      // The URL's chat wins (the effect above opens it). A chatId this user's list does not hold is
      // about to be dropped from the URL (CLE-2851), so it does not use up the staff restore below.
      if (chats.some((c) => c.id === chatIdFromUrl)) {
        firstRestoreDoneRef.current = true;
      }
      return;
    }

    // 2026-10-29 — the staff widget's first restore after mounting (a reload, a bookmark): its last
    // chat (stored per account) unless it has ended, else the chat Clear Health support is on — it
    // has no chat list to find either in. Once only, so New chat is never pulled back into a support
    // chat. A list still holding another account's chats (the account just changed) waits for this
    // account's.
    if (isStaffBot && !firstRestoreDoneRef.current) {
      if (chats.some((c) => c.accountId && c.accountId !== accountId)) return;
      firstRestoreDoneRef.current = true;
      if (!currentChatId) {
        const storedChatId = lastSelectedChatId;
        // A stored id that is no longer listed, or whose chat has ended, is forgotten — so it is
        // neither re-opened below nor kept in storage. Only if nothing has replaced it meanwhile.
        if (isStoredChatStale(chats, storedChatId)) {
          setLastSelectedChatId((latest) =>
            latest === storedChatId ? null : latest
          );
        }
        const chat = pickChatToRestore(chats, storedChatId);
        if (chat) handleChatSelect(chat);
        return;
      }
    }

    // Not when that chat, from this same list, is already open: opening it changed
    // lastSelectedChatId and re-ran this effect (shouldReopenLastChat).
    if (lastSelectedChatId) {
      const lastChat = chats.find((c) => c.id === lastSelectedChatId);
      if (
        lastChat &&
        shouldReopenLastChat({
          lastChat,
          currentChatId,
          loadedChat: loadedChatRef.current,
        })
      ) {
        handleChatSelect(lastChat);
      }
    }
  }, [isOpen, chats, lastSelectedChatId, searchParams]);

  const handleSendMessage = async (messageContent?: string) => {
    const messageToSend = messageContent || inputMessage.trim();
    if (!messageToSend || isLoading) return;
    // CLE-3043 — an ended chat takes no more messages; "New chat" starts another.
    if (isStaffBot && supportStatus === "CLOSED") return;
    // CLE-3043 — while support is on the chat the API stores the message and the bot does not answer.
    setPendingBotReply(!isStaffBot || !isSupportActive(supportStatus));

    const userMessage: IChatMessage = {
      role: "user",
      content: messageToSend,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    setInputMessage("");

    try {
      const headers = chatHeaders();

      // Use different send message endpoints based on chat type
      const sendMessageUrl =
        chatType === "ContractManagementBot"
          ? `${API_BASE_URL}/chats/send_message` // Auth0 auth
          : `${API_BASE_URL}/chats/patient/send_message`; // Patient auth

      const response = await fetch(sendMessageUrl, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({
          chatId: currentChatId,
          accountId,
          type: chatType,
          message: messageToSend,
          data: contextData,
        }),
      });

      const result = await response.json();

      // CLE-2851 — a contract chat this user may not continue is refused with a 403. Forget it, so the
      // next message starts a fresh chat rather than failing the same way again.
      if (!response.ok) {
        // CLE-3043 — support ended this chat: show the API's sentence, and the closed state's New chat.
        // The API stored nothing, so the bubble comes back out and the text goes back in the input —
        // New chat, then send it again.
        if (isStaffBot && response.status === 409) {
          setSupportStatus("CLOSED");
          setInputMessage(messageToSend);
          setMessages((prev) => [
            ...withoutOptimisticMessage(prev, userMessage),
            localSystemLine(
              typeof result.data === "string"
                ? result.data
                : "This chat has ended. Start a new chat."
            ),
          ]);
        }
        if (response.status === 403 && currentChatId) {
          setCurrentChatId(null);
          setSelectedChatId(null);
          setLastSelectedChatId(null);
          updateUrlWithChatId(null);
          if (isStaffBot) resetSupportState();
          setMessages([
            userMessage,
            {
              role: "assistant",
              content:
                "That conversation isn't available to you. Send your message again to start a new chat.",
              timestamp: new Date().toISOString(),
            },
          ]);
        }
        throw new Error(
          typeof result.data === "string" ? result.data : "Message failed"
        );
      }

      const responseData = result.data;

      // If this was a new chat, set the chat ID and update URL
      if (!currentChatId) {
        setCurrentChatId(responseData.chatId);
        setSelectedChatId(responseData.chatId);
        setLastSelectedChatId(responseData.chatId);
        updateUrlWithChatId(responseData.chatId);
      }

      // CLE-3043 — `response: null` means support (not the bot) is on the chat: nothing to render.
      if (isStaffBot && responseData.supportStatus) {
        setSupportStatus(responseData.supportStatus);
      }
      // CLE-3043 review — the bot escalated this turn: its reply is discarded server-side and the
      // escalation message is a system line in the chat. Read the chat now rather than waiting 4 s for
      // the WAITING poll's first tick, so the line shows at once.
      if (
        isStaffBot &&
        responseData.response == null &&
        responseData.chatId &&
        isSupportActive(responseData.supportStatus)
      ) {
        currentChatIdRef.current = responseData.chatId;
        refreshSupportChat(responseData.chatId);
      }
      if (!isStaffBot || responseData.response != null) {
        const assistantMessage: IChatMessage = {
          role: "assistant",
          content: responseData.response,
          timestamp: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, assistantMessage]);
      }

      // Refetch chats to update the list
      fetchChats();
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setIsLoading(false);
      // Focus the input after sending a message
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  /**
   * CLE-3043 — hand the chat to Clear Health support. Sends what is in the input (may be blank — the
   * API then stores a standard request), creates the chat when there is none yet, and switches the
   * widget to WAITING; polling then brings in the "Sent to Clear Health support" line and, later,
   * the support person's replies.
   */
  const handleContactSupport = async () => {
    if (!isStaffBot || isLoading) return;
    const typed = inputMessage.trim();

    setIsLoading(true);
    setPendingBotReply(false);
    setInputMessage("");
    const optimistic: IChatMessage | null = typed
      ? { role: "user", content: typed, timestamp: new Date().toISOString() }
      : null;
    if (optimistic) {
      setMessages((prev) => [...prev, optimistic]);
    }

    try {
      const response = await fetch(`${API_BASE_URL}/chats/send_message`, {
        method: "POST",
        credentials: "include",
        headers: chatHeaders(),
        body: JSON.stringify({
          chatId: currentChatId,
          accountId,
          type: chatType,
          message: typed,
          escalate: true,
          data: { ...contextData, pageUrl: currentPageUrl() },
        }),
      });
      const result = await response.json();

      if (!response.ok) {
        if (response.status === 409) {
          setSupportStatus("CLOSED");
          // The chat had ended and nothing was stored: take the bubble back, restore the text.
          if (optimistic) {
            setMessages((prev) => withoutOptimisticMessage(prev, optimistic));
            setInputMessage(optimistic.content);
          }
        }
        const contact = support ?? SUPPORT_CONTACT_FALLBACK;
        setMessages((prev) => [
          ...prev,
          localSystemLine(
            typeof result.data === "string" && response.status === 409
              ? result.data
              : `We couldn't reach Clear Health support from here. Email ${contact.email} or call ${contact.phone}.`
          ),
        ]);
        return;
      }

      const responseData = result.data;
      const chatId: string | undefined = responseData?.chatId ?? undefined;

      if (!currentChatId && chatId) {
        setCurrentChatId(chatId);
        setSelectedChatId(chatId);
        setLastSelectedChatId(chatId);
        updateUrlWithChatId(chatId);
      }
      // A blank request is stored as the API's standard sentence — show it as the user's message.
      if (!typed && typeof responseData?.message === "string") {
        setMessages((prev) => [
          ...prev,
          {
            role: "user",
            content: responseData.message,
            timestamp: new Date().toISOString(),
          },
        ]);
      }
      setSupportStatus(responseData?.supportStatus ?? "WAITING");
      setEscalatedAt(new Date().toISOString());
      if (responseData?.support) setEscalationSupport(responseData.support);
      setNow(Date.now());

      if (chatId) {
        currentChatIdRef.current = chatId;
        refreshSupportChat(chatId);
      }
      fetchChats();
    } catch (error) {
      console.error("Error contacting support:", error);
      const contact = support ?? SUPPORT_CONTACT_FALLBACK;
      setMessages((prev) => [
        ...prev,
        localSystemLine(
          `We couldn't reach Clear Health support from here. Email ${contact.email} or call ${contact.phone}.`
        ),
      ]);
    } finally {
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleNewChat = () => {
    firstRestoreDoneRef.current = true;
    setCurrentChatId(null);
    setSelectedChatId(null);
    setLastSelectedChatId(null);
    setMessages([]);
    updateUrlWithChatId(null);
    if (isStaffBot) resetSupportState();
    // Focus the input when starting a new chat
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Auto-resize textarea on mobile for better UX
  const handleTextareaResize = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputMessage(e.target.value);
    if (isMobile && e.target) {
      // Reset height to auto to get correct scrollHeight
      e.target.style.height = "auto";
      // Set height to scrollHeight but limit to max-height
      const maxHeight = 100; // Max height in pixels
      const newHeight = Math.min(e.target.scrollHeight, maxHeight);
      e.target.style.height = `${newHeight}px`;
    }
  };

  // Get default title and welcome message based on chat type. The staff defaults (2026-10-29) are
  // the same ones StaffChatWidget passes, so a staff mount without props looks the same.
  const defaultTitle =
    chatType === "PatientChatBot" ? "Clear Health AI" : STAFF_WIDGET_TITLE;

  const defaultSubtitle =
    chatType === "PatientChatBot"
      ? "AI billing assistant"
      : STAFF_WIDGET_SUBTITLE;

  const patientGreeting = patientFirstName
    ? `Hi ${patientFirstName}`
    : "Hi there";

  const defaultWelcomeMessage =
    chatType === "PatientChatBot"
      ? `${patientGreeting} — I can explain estimates, summarize benefits, or draft patient-friendly messages. Try one of these:`
      : buildStaffGreeting(userName);

  // The patient bot's two fixed questions; none for the staff bot. See chatWidget.helpers.ts.
  const faqQuestions = resolveSuggestedQuestions(chatType);

  const handleFaqClick = (question: string) => {
    handleSendMessage(question);
  };

  const displayTitle = title || defaultTitle;
  const displaySubtitle = subtitle ?? defaultSubtitle;
  const displayWelcomeMessage = welcomeMessage || defaultWelcomeMessage;
  // The patient widget's history row, as it always was.
  const patientChatPreview = (chat: IChat): string =>
    chat.chatMessages && chat.chatMessages.length > 0
      ? `${chat.chatMessages[0].content.slice(0, isMobile ? 30 : 50)}...`
      : "New conversation";

  // CLE-3043 (Ani, 2026-11-03) — the staff bot has its history list back, with "+ New chat" in it.
  const showChatList = showsChatList(chatType, multipleChatsSupported);
  // CLE-3043 — chats support answered after the user last saw them (the pill's badge, the rows' dot).
  // Storage is read once per list or seen-mark change (seenVersion), never per render or per row.
  const seenMap = useMemo<Record<string, string | null>>(
    () => (isStaffBot ? buildSeenMap(listedChats, browserChatIdStorage()) : {}),
    [isStaffBot, listedChats, seenVersion]
  );
  const unreadSupportCount = isStaffBot
    ? countUnreadSupportReplies(listedChats, seenMap)
    : 0;

  const closeChat = () => {
    markCurrentChatSeen(currentChatId, messages);
    setIsOpen(false);
    updateUrlWithChatId(null);
  };

  /**
   * CLE-3043 (Ani, 2026-11-03) — end the chat with Clear Health support (POST /chats/:id/end), then
   * close the panel. If it fails the panel stays open with a line saying so.
   */
  const endSupportChatAndClose = async (chatId: string) => {
    try {
      const chat = await endStaffChat(chatId).unwrap();
      if (currentChatIdRef.current === chatId) {
        setMessages((prev) => mergeChatMessages(prev, chat?.chatMessages));
        setSupportStatus("CLOSED");
      }
      setChats((prev) =>
        prev.map((c) =>
          c.id === chatId ? { ...c, supportStatus: "CLOSED" } : c
        )
      );
      closeChat();
    } catch (error) {
      console.error("Error ending the support chat:", error);
      const contact = support ?? SUPPORT_CONTACT_FALLBACK;
      setMessages((prev) => [
        ...prev,
        localSystemLine(
          `We couldn't end this chat. Try again, or email ${contact.email}.`
        ),
      ]);
    }
  };

  /**
   * The header's X (Ani, 2026-11-03). With the bot (or on an ended chat) it only closes the panel. On a
   * chat with Clear Health support (WAITING / HUMAN) it ends that chat too, after one confirm.
   */
  const handleCloseClick = () => {
    const chatId = currentChatId;
    if (isStaffBot && chatId && isSupportActive(supportStatus)) {
      confirmDialog({
        message: END_SUPPORT_CHAT_CONFIRM.message,
        acceptLabel: END_SUPPORT_CHAT_CONFIRM.acceptLabel,
        rejectLabel: END_SUPPORT_CHAT_CONFIRM.rejectLabel,
        accept: () => {
          endSupportChatAndClose(chatId);
        },
      });
      return;
    }
    closeChat();
  };

  // 2026-10-29 — the fixed staff widget always carries the lift classes; they only move it while a
  // bottom action bar (PageActionBar) is mounted and marks the body (see ChatWidget.css), so the
  // bar itself is the one source of truth. The patient widget never lifts.
  const isLifted = isStaffBot && position === "fixed";

  // 2026-10-29 — the fixed staff widget puts its pill in a fixed corner row. The row lifts above a
  // bottom action bar by the rule in clear-theme-overrides.css.
  const usesCornerRow = isStaffBot && position === "fixed";

  // The pill stays put whether the chat is open or closed — it toggles it.
  const questionsPill = (
    <QuestionsPill
      inline={position === "inline" || usesCornerRow}
      ariaLabel={displayTitle}
      isOpen={isOpen}
      badgeCount={unreadSupportCount}
      onClick={() => (isOpen ? closeChat() : setIsOpen(true))}
    />
  );

  const togglePill = usesCornerRow ? (
    <div className="chat-widget-corner-row">{questionsPill}</div>
  ) : (
    questionsPill
  );

  // CLE-3043 — the status line above the input while support is involved (staff widget only).
  const supportBanner = isStaffBot
    ? getSupportBanner({
        supportStatus,
        support,
        supportAgentName,
        escalatedAt,
        now,
        userEmail,
      })
    : null;
  const isChatClosed = isStaffBot && supportStatus === "CLOSED";
  const inputPlaceholder = (() => {
    if (!isStaffBot) return "Ask me anything...";
    if (isChatClosed) return "This chat has ended";
    if (isSupportActive(supportStatus))
      return "Message Clear Health support...";
    return "Ask me anything...";
  })();

  if (!isVisible) {
    return null;
  }

  if (!isOpen) {
    return togglePill;
  }

  return (
    <>
      <div
        className={`chat-widget ${
          position === "inline" ? "chat-widget-inline" : ""
        } ${showChatList ? "chat-widget-with-list" : ""}${
          isLifted ? " chat-widget-above-action-bar" : ""
        }`}
      >
        <div className="chat-header">
          <div className="chat-header-main">
            <div className="chat-header-icon">
              <SparkleIcon />
            </div>
            <div className="chat-header-text">
              <h3>{displayTitle}</h3>
              {displaySubtitle && (
                <div className="chat-header-subtitle">{displaySubtitle}</div>
              )}
            </div>
          </div>
          <button
            type="button"
            className="close-button"
            aria-label="Close chat"
            onClick={handleCloseClick}
          >
            ×
          </button>
        </div>

        <div className="chat-body">
          {/* Chat List Sidebar — the patient bot when its model supports several chats; the staff bot
              always (CLE-3043, 2026-11-03), with only this user's chats on this hospital. */}
          {showChatList && (
            <div className="chat-list">
              <div className="chat-list-header">
                <button
                  type="button"
                  className="new-chat-button"
                  onClick={handleNewChat}
                >
                  + New Chat
                </button>
              </div>
              <div className="chat-list-items">
                {listedChats.map((chat) => {
                  // The chat on screen shows its live status (the 4 s poll), not the list's copy.
                  const tag = isStaffBot
                    ? supportStatusTag(
                        chat.id === currentChatId
                          ? supportStatus
                          : chat.supportStatus
                      )
                    : null;
                  const hasNewReply =
                    isStaffBot &&
                    hasUnreadSupportReply(
                      chat.lastSupportMessageAt,
                      seenMap[chat.id]
                    );
                  return (
                    <div
                      key={chat.id}
                      className={`chat-list-item ${
                        selectedChatId === chat.id ? "active" : ""
                      }`}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleChatSelect(chat)}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleChatSelect(chat)
                      }
                    >
                      <div className="chat-preview">
                        {hasNewReply && (
                          <span
                            className="chat-new-reply-dot"
                            role="img"
                            aria-label="New reply"
                            title="New reply"
                          />
                        )}
                        {isStaffBot
                          ? chatPreview(chat.chatMessages)
                          : patientChatPreview(chat)}
                      </div>
                      <div className="chat-date">
                        {new Date(chat.createdAt).toLocaleDateString(
                          undefined,
                          isMobile
                            ? { month: "short", day: "numeric" }
                            : undefined
                        )}
                        {tag && (
                          <span
                            className={`chat-status-tag chat-status-tag-${String(
                              chat.id === currentChatId
                                ? supportStatus
                                : chat.supportStatus
                            ).toLowerCase()}`}
                          >
                            {tag}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Chat Messages Area */}
          <div className={`chat-messages ${!showChatList ? "full-width" : ""}`}>
            <div className="messages-container">
              {messages.length === 0 && (
                <>
                  <div className="welcome-row">
                    <div className="assistant-avatar">
                      <SparkleIcon />
                    </div>
                    <div className="welcome-message">
                      {displayWelcomeMessage}
                    </div>
                  </div>
                  {faqQuestions.length > 0 && (
                    <div className="faq-container">
                      <div className="faq-questions">
                        {faqQuestions.map((question, index) => (
                          <button
                            key={index}
                            type="button"
                            className="faq-button"
                            onClick={() => handleFaqClick(question)}
                          >
                            {question}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
              {messages.map((message, index) => {
                const kind = getMessageKind(message);

                // CLE-3043 — "Sent to Clear Health support", "<Name> joined", "Chat ended…".
                if (kind === "system") {
                  return (
                    <div key={index} className="system-message">
                      {message.content}
                    </div>
                  );
                }

                return (
                  <div
                    key={index}
                    className={`message ${
                      message.role === "user"
                        ? "user-message"
                        : "assistant-message"
                    }${kind === "support" ? " support-message" : ""}`}
                  >
                    {kind === "support" && (
                      <div className="message-author">
                        {message.authorName || "Clear Health support"}
                        <span className="message-author-org">
                          {" "}
                          · Clear Health
                        </span>
                      </div>
                    )}
                    <div
                      className="message-content"
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      {message.content}
                    </div>
                    <div className="message-time">
                      {new Date(message.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                );
              })}
              {isLoading && (!isStaffBot || pendingBotReply) && (
                <div className="message assistant-message">
                  <div className="message-content">
                    <div className="typing-indicator">
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="chat-input">
              {supportBanner && (
                <div
                  className={`support-banner support-banner-${supportBanner.tone}`}
                  role="status"
                >
                  <span>{supportBanner.text}</span>
                  {isChatClosed && (
                    <button
                      type="button"
                      className="support-new-chat-button"
                      onClick={handleNewChat}
                    >
                      New chat
                    </button>
                  )}
                </div>
              )}
              <div className="input-controls">
                <textarea
                  ref={inputRef}
                  value={inputMessage}
                  onChange={handleTextareaResize}
                  onKeyPress={handleKeyPress}
                  placeholder={inputPlaceholder}
                  rows={1}
                  disabled={isLoading || isChatClosed}
                  style={isMobile ? { resize: "none", overflow: "hidden" } : {}}
                />
                <button
                  type="button"
                  onClick={() => handleSendMessage()}
                  disabled={!inputMessage.trim() || isLoading || isChatClosed}
                  className="send-button"
                  aria-label="Send"
                >
                  <SendIcon />
                </button>
              </div>
              {/* CLE-3043 — staff widget only, never the patient one. Hidden once support is on
                  the chat (the banner above takes over) or the chat has ended. */}
              {isStaffBot && supportStatus === "NONE" && (
                <button
                  type="button"
                  className="contact-support-button"
                  onClick={handleContactSupport}
                  disabled={isLoading}
                >
                  {CONTACT_SUPPORT_LABEL}
                </button>
              )}
              <div className="chat-disclaimer">
                AI assistant can make mistakes. If unsure, call us.
              </div>
            </div>
          </div>
        </div>
      </div>
      {togglePill}
    </>
  );
}
